import billingApi from '@/lib/billingApi'

export type NodeInfo = {
  id: number
  name: string
  fqdn: string
  scheme?: string
  port?: number
  browser_port?: number
  latency_url?: string
  behind_proxy?: boolean
  public?: boolean
}

export type LocationInfo = {
  id: number
  short?: string
  long?: string
  nodes: NodeInfo[]
  tier_availability?: Record<string, boolean>
  package_available?: boolean
}

const LOCATION_CACHE_KEY = 'locationsCacheV2'
const LOCATION_CACHE_MAX_AGE_MS = 30_000
const LATENCY_CACHE_KEY = 'latenciesCacheV2'
const LATENCY_PATH = '/api/latency'
const SAMPLE_COUNT = 10
const WARMUP_COUNT = 1
const SAMPLE_DELAY_MS = 75
const MAX_NODES_PER_LOCATION = 3

export async function listLocations(): Promise<LocationInfo[]> {
  const now = Date.now()
  try {
    const raw = sessionStorage.getItem(LOCATION_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { ts: number; data: LocationInfo[] }
      if (parsed && Array.isArray(parsed.data) && now - parsed.ts < LOCATION_CACHE_MAX_AGE_MS) {
        return parsed.data
      }
    }
  } catch { /* session storage is optional */ }

  const res = await billingApi.get<{ data: LocationInfo[] }>('/subscription/locations')
  const data = Array.isArray(res.data?.data) ? res.data.data : []
  try { sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ ts: now, data })) } catch { /* session storage is optional */ }
  return data
}

export async function prefetchLocations(): Promise<void> {
  try { await listLocations() } catch { /* prefetch is best-effort */ }
}

function median(values: number[]): number {
  if (!values.length) return Number.POSITIVE_INFINITY
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle]
}

function latencyWebSocketUrl(node: NodeInfo): string | null {
  const configuredUrl = node.latency_url?.trim()
  if (configuredUrl) {
    try {
      const url = new URL(configuredUrl)
      if (url.protocol === 'ws:' || url.protocol === 'wss:') return url.toString()
    } catch { /* invalid configured URL falls through to node coordinates */ }
  }

  const host = node.fqdn?.trim()
  if (!host || node.public === false) return null
  const scheme = node.scheme === 'http' ? 'ws' : 'wss'
  const defaultPort = scheme === 'wss' ? 443 : 80
  const port = node.port ?? defaultPort
  const portSuffix = port === defaultPort ? '' : `:${port}`
  return `${scheme}://${host}${portSuffix}${LATENCY_PATH}`
}

/**
 * Measures application-level RTT over one persistent socket. DNS, TCP, and TLS
 * setup complete before the first message and are therefore excluded. Wings
 * echoes only a four-byte sequence number; the first echo is a discarded warmup.
 */
export function measureLatencyToNode(node: NodeInfo, timeoutMs = 10_000): Promise<number> {
  const url = latencyWebSocketUrl(node)
  if (!url) return Promise.resolve(Number.POSITIVE_INFINITY)

  return new Promise((resolve) => {
    let socket: WebSocket | null = null
    let sequence = 0
    let sentAt = 0
    let settled = false
    let nextSampleTimer: ReturnType<typeof setTimeout> | null = null
    const samples: number[] = []

    const finish = (value: number) => {
      if (settled) return
      settled = true
      clearTimeout(overallTimer)
      if (nextSampleTimer) clearTimeout(nextSampleTimer)
      if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'measurement complete')
      resolve(value)
    }

    const overallTimer = setTimeout(() => finish(Number.POSITIVE_INFINITY), timeoutMs)

    const sendSample = () => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        finish(Number.POSITIVE_INFINITY)
        return
      }
      const payload = new ArrayBuffer(4)
      new DataView(payload).setUint32(0, sequence, false)
      sentAt = performance.now()
      socket.send(payload)
    }

    try {
      socket = new WebSocket(url)
      socket.binaryType = 'arraybuffer'
    } catch {
      finish(Number.POSITIVE_INFINITY)
      return
    }

    socket.addEventListener('open', sendSample)
    socket.addEventListener('message', (event) => {
      if (!(event.data instanceof ArrayBuffer) || event.data.byteLength !== 4) {
        finish(Number.POSITIVE_INFINITY)
        return
      }
      const echoedSequence = new DataView(event.data).getUint32(0, false)
      if (echoedSequence !== sequence) {
        finish(Number.POSITIVE_INFINITY)
        return
      }

      const elapsed = performance.now() - sentAt
      if (sequence >= WARMUP_COUNT) samples.push(elapsed)
      sequence += 1

      if (samples.length >= SAMPLE_COUNT) {
        const sorted = samples.sort((a, b) => a - b)
        const trimmed = sorted.length >= 5 ? sorted.slice(1, -1) : sorted
        finish(median(trimmed))
        return
      }

      nextSampleTimer = setTimeout(sendSample, SAMPLE_DELAY_MS)
    })
    socket.addEventListener('error', () => finish(Number.POSITIVE_INFINITY))
    socket.addEventListener('close', () => finish(Number.POSITIVE_INFINITY))
  })
}

/** Measure representative nodes concurrently and return the median successful RTT. */
export async function measureLatencyToLocation(location: LocationInfo, timeoutMs = 10_000): Promise<number> {
  const nodes = location.nodes
    .filter(node => latencyWebSocketUrl(node) !== null)
    .slice(0, MAX_NODES_PER_LOCATION)
  if (!nodes.length) return Number.POSITIVE_INFINITY

  const results = await Promise.all(nodes.map(node => measureLatencyToNode(node, timeoutMs)))
  return median(results.filter(Number.isFinite))
}

type LatencyCache = { ts: number; data: Record<number, number> }

export function getCachedLatencies(maxAgeMs = 60_000): Record<number, number> {
  try {
    const raw = sessionStorage.getItem(LATENCY_CACHE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as LatencyCache
    if (!parsed || typeof parsed !== 'object' || typeof parsed.ts !== 'number') return {}
    if (Date.now() - parsed.ts > maxAgeMs) return {}
    return parsed.data || {}
  } catch {
    return {}
  }
}

function setCachedLatencies(data: Record<number, number>) {
  try {
    const raw = sessionStorage.getItem(LATENCY_CACHE_KEY)
    let existing: LatencyCache | null = null
    if (raw) {
      try { existing = JSON.parse(raw) as LatencyCache } catch { existing = null }
    }
    const merged = { ...(existing?.data || {}), ...data }
    const payload: LatencyCache = { ts: Date.now(), data: merged }
    sessionStorage.setItem(LATENCY_CACHE_KEY, JSON.stringify(payload))
  } catch { /* session storage is optional */ }
}

export function recordLatency(locationId: number, ms: number) {
  if (Number.isFinite(ms)) setCachedLatencies({ [locationId]: ms })
}

export async function warmupLatencies(): Promise<void> {
  try {
    const locations = await listLocations()
    await Promise.all(locations.map(async (location) => {
      const ms = await measureLatencyToLocation(location)
      recordLatency(location.id, ms)
    }))
  } catch { /* warmup is best-effort */ }
}

export default {
  listLocations,
  measureLatencyToNode,
  measureLatencyToLocation,
  prefetchLocations,
  getCachedLatencies,
  warmupLatencies,
  recordLatency,
}
